import { delayParts } from '@/lib/delay/format';
import type {
  DisruptionCaseRow,
  DisruptionListRow,
  DisruptionOverviewRow,
  IDisruptionDeniedBoarding,
} from '@/lib/disruptions/types';

// The PURE derivation of the disruptions list (/disruptions): WHEN each open disruption
// happened (today / yesterday / no movement), the ONE state badge of a row, its
// flights and the few numbers the operator acts on. The route renders, this derives;
// tests pin it. Copy is addressed by i18n KEY of the `disruptions` namespace — this
// module holds no language.

/* ── When: today · yesterday · no movement ──────────────────────────────────── */

/**
 * The day group of a disruption on the board.
 *
 * THE RULE. `DisruptionListRow` / `DisruptionOverviewRow` carry ONE instant:
 * `detectedAt`. There is no `updatedAt` / `lastActivity` on the row (the reconciler's
 * `staleness` marker is absent on most rows, so it cannot carry the rule). So:
 *
 *   - `today`     — detected on the operator's current LOCAL calendar day (open, or
 *                   contained today: what happened today stays in view);
 *   - `yesterday` — detected on the local day before (same);
 *   - `stale`     — «Sin movimiento»: still OPEN (`isOpen`: DETECTED) AND detected before
 *                   yesterday's local midnight, i.e. open for two calendar days or more.
 *
 * The overview is NOT open-only: it lists every non-terminal disruption (operator-bff
 * `overview()` only drops CLOSED / MERGED), so CONTAINED rows come with it. A contained
 * row detected before yesterday is not a zombie — it is resolved — so it leaves this
 * board (`groupByDay` drops it); the «Pasadas» tab lists it.
 *
 * «Local» is the browser's time zone (the operator works the airport it sits in);
 * `timeZone` only exists so tests can pin one. A `detectedAt` in the future (clock
 * skew) counts as today. An unreadable `detectedAt` cannot be placed in time: an open
 * row with one goes to `stale`, never to «Hoy», and its row reads «sin dato» for the age.
 */
export type DayGroup = 'today' | 'yesterday' | 'stale';

export const DAY_GROUPS: readonly DayGroup[] = ['today', 'yesterday', 'stale'];

const DAY_MS = 86_400_000;

const formatters = new Map<string, Intl.DateTimeFormat>();

function formatterFor(timeZone: string | undefined): Intl.DateTimeFormat {
  const key = timeZone ?? '';
  let f = formatters.get(key);
  if (!f) {
    f = new Intl.DateTimeFormat('en-US', {
      ...(timeZone ? { timeZone } : {}),
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
    });
    formatters.set(key, f);
  }
  return f;
}

/** The calendar day of an instant in `timeZone` (browser zone when absent), as a day count. */
export function localDay(ms: number, timeZone?: string): number {
  const parts = formatterFor(timeZone).formatToParts(new Date(ms));
  const part = (type: Intl.DateTimeFormatPartTypes): number =>
    Number(parts.find((p) => p.type === type)?.value);
  return Math.round(Date.UTC(part('year'), part('month') - 1, part('day')) / DAY_MS);
}

/** Which group a disruption detected at `detectedAt` falls in, seen at `now`. */
export function dayGroupOf(detectedAt: string, now: number, timeZone?: string): DayGroup {
  const at = Date.parse(detectedAt);
  if (!Number.isFinite(at)) return 'stale';
  const diff = localDay(now, timeZone) - localDay(at, timeZone);
  if (diff <= 0) return 'today';
  if (diff === 1) return 'yesterday';
  return 'stale';
}

export type DayGroups<R> = Record<DayGroup, R[]>;

/**
 * The rows split by day group, each group newest first (unreadable dates last). A row
 * that is not open and was detected before yesterday is left out (see the rule above).
 */
export function groupByDay<R extends Pick<DisruptionListRow, 'detectedAt' | 'status'>>(
  rows: readonly R[],
  now: number,
  timeZone?: string,
): DayGroups<R> {
  const groups: DayGroups<R> = { today: [], yesterday: [], stale: [] };
  for (const row of rows) {
    const group = dayGroupOf(row.detectedAt, now, timeZone);
    if (group === 'stale' && !isOpen(row)) continue;
    groups[group].push(row);
  }
  const at = (r: R): number => {
    const ms = Date.parse(r.detectedAt);
    return Number.isFinite(ms) ? ms : -Infinity;
  };
  for (const g of DAY_GROUPS) groups[g].sort((a, b) => at(b) - at(a));
  return groups;
}

/* ── The ONE state badge (the tones of «Operar la disrupción») ──────────────── */

/** Same tones as Operar's header badge (`OperateTone`). */
export type BoardTone = 'neutral' | 'delay' | 'cancelled' | 'resolved';

export interface IBoardBadge {
  tone: BoardTone;
  /** i18n key in the `disruptions` namespace (`board.badge.*`). */
  key: string;
  params?: Record<string, number>;
}

/* ── Denied boarding («Sobreventa», `flightStatus@v2`) — shared with Operar ─────── */

/** The flight kinds graver than a denied boarding on the lattice DELAY < DENIED_BOARDING < DIVERTED < CANCELLED. */
const GRAVER_THAN_DENIAL: ReadonlySet<string> = new Set(['DIVERTED', 'CANCELLED']);

/**
 * The denied-boarding counts when well formed (finite, non-negative) — the BFF's
 * `flightStatus.deniedBoarding@v2`, on the list, overview and detail rows alike.
 * `undefined` when the disruption has no denied-boarding case.
 */
export function deniedCountsOf(
  row: Pick<DisruptionListRow, 'flightStatus'>,
): IDisruptionDeniedBoarding | undefined {
  const d = row.flightStatus?.deniedBoarding;
  const ok = (n: unknown): n is number => typeof n === 'number' && Number.isFinite(n) && n >= 0;
  return d && ok(d.voluntary) && ok(d.involuntary)
    ? { voluntary: d.voluntary, involuntary: d.involuntary }
    : undefined;
}

/**
 * The disruption is a denied boarding: the BFF folded it so (`kind === 'DENIED_BOARDING'`),
 * or it carries the denied counts and no graver flight kind (a CANCELLED / DIVERTED
 * flight wins the lattice — the counts stay, the word is theirs). A null kind with the
 * counts present (a fold that has not reached the row, a kind this UI does not know) is
 * a denial, never a cancellation. ONE predicate for the board and Operar (`kindOf`).
 */
export function deniedOf(row: Pick<DisruptionListRow, 'flightStatus' | 'worstEventKind'>): boolean {
  const kind: string | null = row.flightStatus?.kind ?? null;
  if (kind === 'DENIED_BOARDING') return true;
  if (!deniedCountsOf(row)) return false;
  return !GRAVER_THAN_DENIAL.has(kind ?? '') && !GRAVER_THAN_DENIAL.has(row.worstEventKind ?? '');
}

/** «DEMORADO +X» by the delay's size — the same split as Operar's `delayBadge`. */
function delayedBadge(minutes: number | undefined): IBoardBadge {
  if (!minutes || minutes <= 0) return { tone: 'delay', key: 'board.badge.delayed' };
  const { key, h, m } = delayParts(minutes);
  const suffix = key.slice(key.lastIndexOf('.') + 1); // minutes | hours | hoursMinutes
  const name = suffix.charAt(0).toUpperCase() + suffix.slice(1);
  return { tone: 'delay', key: `board.badge.delayed${name}`, params: { h, m } };
}

/**
 * A row's state badge — the SAME word Operar's header shows when the row is opened
 * (`operar/operate-model.ts` `kindOf`, its precedence as far as a row can tell; pinned
 * by `board-model.test.ts` against `operateView`):
 *
 *   1. MERGED                    → «FUSIONADA» (neutral) — only the past list has them;
 *   2. CONTAINED / RESOLVED      → «TODO RESUELTO» (Operar rule 2);
 *   3. a delay escalated         → «CANCELADO» (rule 3: the grace window / its release);
 *   4. a flight CANCELLED        → «CANCELADO» (rule 5: `flightStatus.kind` / `worstEventKind`);
 *   5. a DENIED BOARDING         → «SOBREVENTA» (rule 6, `flightStatus@v2`): the kind says
 *      so, or the denied counts are on the row with no graver flight kind — a null kind
 *      with `deniedBoarding` present never reads «CANCELADO» (Operar's `deniedOf`). In
 *      the rose of «they need a new flight», so it is never a plain delay;
 *   6. a DELAY, or a delay summary on the row (`maxDelayMinutes`, the sign Operar's
 *      delay timelines exist) → «DEMORADO +X» with the worst delay seen (rule 7);
 *   7. what is left — a DIVERTED flight, a hand-declared disruption with no flight kind
 *      → «CANCELADO» (rule 8: Operar operates it as the reaccommodation it needs).
 *
 * What a row cannot know: Operar's rule 4 (every step settled → «TODO RESUELTO» while
 * still DETECTED) reads resolution state, which the list does not carry.
 */
export function rowBadge(row: DisruptionListRow): IBoardBadge {
  if (row.status === 'MERGED') return { tone: 'neutral', key: 'board.badge.merged' };
  if (row.status === 'CONTAINED' || row.status === 'RESOLVED') {
    return { tone: 'resolved', key: 'board.badge.resolved' };
  }
  const fs = row.flightStatus;
  const cancelled: IBoardBadge = { tone: 'cancelled', key: 'board.badge.cancelled' };
  if (fs?.escalated) return cancelled;
  if (fs?.kind === 'CANCELLED' || row.worstEventKind === 'CANCELLED') return cancelled;
  if (deniedOf(row)) return { tone: 'cancelled', key: 'board.badge.deniedBoarding' };
  if ((fs?.kind ?? row.worstEventKind) === 'DELAY' || typeof fs?.maxDelayMinutes === 'number') {
    return delayedBadge(fs?.maxDelayMinutes);
  }
  return cancelled;
}

/** The badge chip per tone — the raw status colours of Operar's header (`operar-header.tsx`). */
export const BOARD_BADGE_TONE: Readonly<Record<BoardTone, string>> = {
  neutral: 'bg-muted text-muted-foreground',
  delay: 'bg-amber-100 text-amber-900 dark:bg-amber-500/15 dark:text-amber-300',
  cancelled: 'bg-rose-100 text-rose-900 dark:bg-rose-500/15 dark:text-rose-300',
  resolved: 'bg-emerald-100 text-emerald-900 dark:bg-emerald-500/15 dark:text-emerald-300',
};

/* ── The flights (the row's title) ───────────────────────────────────────────── */

export interface IBoardFlight {
  /** As the airline prints it (`LA800`). */
  code: string;
  origin?: string;
  destination?: string;
  /** Its scheduled departure (ISO UTC) — WHEN the flight is (PO 2026-11-01: «es
   *  importante mostrar el vuelo, el día y la hora, si no no sabemos si es de hoy»). */
  departureAt?: string;
}

/** `urn:flight:LA593@2026-08-20T23:45:00Z:…` → `2026-08-20T23:45:00Z` (its STD); `null`
 *  when the URN carries no instant (`urn:flight:LA593-2026-08-20` is a day, not a time). */
export function flightDepartureOf(flightUrn: string): string | null {
  const m = /^urn:flight:[A-Za-z0-9]+@(\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?Z)/.exec(flightUrn);
  return m && Number.isFinite(Date.parse(m[1]!)) ? m[1]! : null;
}

/** `urn:flight:LA593@2026-08-20T23:45:00Z:…` / `urn:flight:LA593-2026-08-20` → `LA593`. */
export function flightCodeOf(flightUrn: string): string | null {
  const m = /^urn:flight:([A-Za-z0-9]+)[@-]/.exec(flightUrn);
  return m ? m[1]!.toUpperCase() : null;
}

/**
 * The flights a row is about. The list row carries no route: the detail's cases do
 * (one case ≈ one flight, with its origin and destination), so the board passes the
 * cached detail when it has it. Without it, the flight codes of a FLIGHT_SET scope
 * (`scope.flightUrns`) — no route invented. `[]` when neither knows a flight: the
 * row then falls back to its title.
 */
export function boardFlights(
  row: Pick<DisruptionListRow, 'scope'>,
  cases?: readonly DisruptionCaseRow[] | null,
): IBoardFlight[] {
  const seen = new Set<string>();
  const out: IBoardFlight[] = [];
  const add = (f: IBoardFlight): void => {
    const key = `${f.code}|${f.origin ?? ''}|${f.destination ?? ''}`;
    if (seen.has(key)) return;
    seen.add(key);
    out.push(f);
  };
  for (const c of Array.isArray(cases) ? cases : []) {
    if (!c.flightNumber) continue;
    const departureAt = c.departureAt ?? (c.flightUrn ? flightDepartureOf(c.flightUrn) : null);
    add({
      code: c.flightNumber,
      ...(c.origin ? { origin: c.origin } : {}),
      ...(c.destination ? { destination: c.destination } : {}),
      ...(departureAt ? { departureAt } : {}),
    });
  }
  if (out.length > 0) return out;
  for (const urn of row.scope.flightUrns ?? []) {
    const code = flightCodeOf(urn);
    const departureAt = flightDepartureOf(urn);
    if (code) add({ code, ...(departureAt ? { departureAt } : {}) });
  }
  return out;
}

/* ── The numbers ─────────────────────────────────────────────────────────────── */

/** Still being worked: DETECTED. CONTAINED / RESOLVED / MERGED are closed for the board. */
export function isOpen(row: Pick<DisruptionListRow, 'status'>): boolean {
  return row.status === 'DETECTED';
}

/**
 * The row's counts are not known yet. The BFF maps a missing per-row summary (cold
 * projector — typical for a disruption declared minutes ago) to an ALL-ZERO rollup
 * (`rollupFromSummary(null)`), so «0 cases · 0 passengers» means «not there yet», not
 * «empty»: it reads «sin dato», never 0. (An older BFF without the field: same.)
 */
export function countsUnknown(row: Pick<DisruptionListRow, 'rollup'>): boolean {
  const r = row.rollup;
  if (!r || typeof r.affectedPassengers !== 'number') return true;
  return r.caseCount === 0 && r.affectedPassengers === 0;
}

/**
 * A plain delay (the badge says DEMORADO): the passengers keep their own flight and
 * Nexa's rebooking waits on it (Operar's delay view) — nobody there is «sin resolver»,
 * even though `unresolvedPassengers` (affected − rebooked) counts them all. An escalated
 * delay reads CANCELADO and is NOT a plain delay.
 */
export function isPlainDelay(row: DisruptionListRow): boolean {
  return rowBadge(row).tone === 'delay';
}

/**
 * The ONE big number of a row: WHAT it counts and how many (`n: null` = «sin dato» —
 * the counts have not landed; never 0).
 *
 *   - `unresolved` — passengers still owed an outcome (affected − rebooked): a
 *                    cancellation, or anything Operar operates as one;
 *   - `passengers` — a plain delay: the passengers on it, who wait for their flight.
 */
export interface IBoardFigure {
  kind: 'unresolved' | 'passengers';
  n: number | null;
}

/**
 * The big number of a row, or `null` for a closed row: there affected − rebooked also
 * counts the passengers who travelled or declined, so it is not «sin resolver» and
 * the row draws no number.
 *
 * Known limit: on an open cancellation, affected − rebooked still counts a passenger
 * who travelled, declined or made their own arrangements (the row carries no
 * resolution state) — a per-row resolved count from the backend would fix it.
 */
export function rowFigure(row: DisruptionOverviewRow): IBoardFigure | null {
  if (!isOpen(row)) return null;
  const unknown = countsUnknown(row);
  if (isPlainDelay(row)) {
    return { kind: 'passengers', n: unknown ? null : row.rollup.affectedPassengers };
  }
  return { kind: 'unresolved', n: unknown ? null : row.unresolvedPassengers };
}

/** A sum of unresolved passengers over some rows. */
export interface IPaxSum {
  /** `null` = rows where it applies exist but none knows its count («sin dato»). */
  n: number | null;
  /** Some rows where it applies do not know their count — `n` is a floor («12+»). */
  partial: boolean;
}

/** Σ unresolved passengers over the OPEN rows that are not plain delays. */
export function unresolvedSum(rows: readonly DisruptionOverviewRow[]): IPaxSum {
  let n: number | null = null;
  let applies = 0;
  let unknown = 0;
  for (const row of rows) {
    if (!isOpen(row) || isPlainDelay(row)) continue;
    applies += 1;
    if (countsUnknown(row)) unknown += 1;
    else n = (n ?? 0) + row.unresolvedPassengers;
  }
  if (applies === 0) return { n: 0, partial: false };
  return { n, partial: n !== null && unknown > 0 };
}

export interface IBoardKpis {
  /** Unresolved passengers over TODAY's and YESTERDAY's rows. */
  unresolved: IPaxSum;
  /** Unresolved passengers held by the «Sin movimiento» rows — kept OUT of the KPI
   *  (days-old counts would drown what is happening now) but said on that group's
   *  header, so a board of zombies never reads as zero. */
  stale: IPaxSum;
}

export function boardKpis(groups: DayGroups<DisruptionOverviewRow>): IBoardKpis {
  return {
    unresolved: unresolvedSum([...groups.today, ...groups.yesterday]),
    stale: unresolvedSum(groups.stale),
  };
}

/* ── «Lo que sigue» on the board ───────────────────────────────────────────── */

/**
 * The disruption the board's navy card points at — the one that needs the operator
 * most — and why:
 *
 *   - `decision` — an open row whose provider report exceeds the policy maximum and
 *                  waits for the operator (`flightStatus.outOfPolicy`, the one flag of
 *                  the list that asks for a person). Newest first.
 *   - `unresolved` — else the open row with the most passengers still unresolved
 *                  (`rowFigure`: a plain delay counts nobody; «sin dato» counts nobody).
 *
 * Today and yesterday only, like the KPI: a «Sin movimiento» zombie is said on its own
 * group, never promoted over what is happening now. `null` when nothing qualifies — the
 * card then names no flight (it never claims «nadie te necesita»: the list row cannot
 * see offers waiting on a passenger).
 */
export interface IBoardNext {
  row: DisruptionOverviewRow;
  reason: 'decision' | 'unresolved';
}

export function nextOnBoard(groups: DayGroups<DisruptionOverviewRow>): IBoardNext | null {
  const recent = [...groups.today, ...groups.yesterday].filter(isOpen);
  // the groups are newest first, so the first flagged row is the newest one
  const decision = recent.find((r) => !!r.flightStatus?.outOfPolicy);
  if (decision) return { row: decision, reason: 'decision' };
  let best: { row: DisruptionOverviewRow; n: number } | null = null;
  for (const row of recent) {
    const figure = rowFigure(row);
    if (figure?.kind !== 'unresolved' || figure.n === null || figure.n <= 0) continue;
    if (!best || figure.n > best.n) best = { row, n: figure.n };
  }
  return best ? { row: best.row, reason: 'unresolved' } : null;
}

/* ── The detail reads for the routes ───────────────────────────────────────── */

/**
 * The disruptions whose detail the board reads for their flights' routes (the list row
 * carries none). Filled group by group, WHOLE groups only, so rows of the same group
 * never read differently: every «Hoy» row first, up to `cap`; then «Ayer», only when all
 * of it fits in what is left; «Sin movimiento» never. The visible effect of the cap: on
 * a day with more than `cap` disruptions, the «Hoy» rows past it (the oldest) keep the
 * fallback title — their scope's flight codes, else their title — and «Ayer» does too.
 */
export function routeLookupUrns(
  groups: DayGroups<Pick<DisruptionListRow, 'urn'>>,
  cap: number,
): string[] {
  const today = groups.today.slice(0, cap);
  const left = cap - today.length;
  const yesterday = groups.yesterday.length <= left ? groups.yesterday : [];
  return [...today, ...yesterday].map((r) => r.urn);
}
